import { _Product, _Variant } from "/global-stores";
import initTheme from "/theme";
import { ProductAdvancedSubscription } from "/product.advanced_subscription";
import { ProductCollapsibleTab } from "/product.collapsible_tab";
import { ProductDynamicText } from "/product.dynamic-text";
import { ProductLabels } from "/product.labels";
import { ProductSubscriptionSelector } from "/product.subscription_selector";
import {
  _Product_liquid,
  _Variant_liquid,
  ProductAddToCartBarSection,
  ProductBlocks,
  ProductBlocksAdvanced_subscription,
  ProductBlocksBuy_buttons,
  ProductBlocksCollapsible_tab,
  ProductBlocksComplementary,
  ProductBlocksDescription,
  ProductBlocksDynamic_buy_buttons,
  ProductBlocksDynamic_text,
  ProductBlocksImage,
  ProductBlocksInventory,
  ProductBlocksPre_order,
  ProductBlocksProduct_group_selector,
  ProductBlocksProduct_sibling,
  ProductBlocksSubscription_selector,
  ProductBlocksTitle,
  ProductBlocksVariant_selector,
  ProductBlocksVariant_upsell,
  ProductSection,
  ProductSiblingsSection,
  ProductBlocksDownload_link,
} from "_types";
import { render } from "preact";
import { ProductAddToCartBar } from "product-add-to-cart-bar";
import { ProductBuyButtons } from "product.buy_buttons";
import { ProductComplementaryItems } from "product.complementary";
import { ProductDescription } from "product.description";
import { DynamicProductBuyButtons } from "product.dynamic_buy_buttons";
import { ProductGalleryHorizontal, ProductGalleryVertical } from "product.gallery";
import { ProductImage } from "product.image";
import { ProductInventorySlider } from "product.inventory";
import { ProductPreOrder } from "product.pre_order";
import { ProductPrice } from "product.price";
import { ProductSibling } from "product.product_sibling";
import { ProductQuantitySelector } from "product.quantity_selector";
import { ProductRating } from "product.rating";
import { ProductShare } from "product.share";
import { ProductSideEffects } from "product.side-effects";
import { ProductSku } from "product.sku";
import { ProductLeadTime } from "product.lead-time";
import { ProductTitle } from "product.title";
import { ProductVariantSelector } from "product.variant_selector";
import { ProductVariantUpsell } from "product.variant_upsell";
import { ProductVendor } from "product.vendor";
import { delay, JSONParse } from "utils";
import { create as createReact } from "zustand";
import { ProductGroupSelector } from "./product.product_group_selector";

export interface SingleSelectedConfigurableProduct {
  stepNumber: number;
  product: _Product;
  selectedVariant: _Variant_liquid;
  quantityToAdd: number;
  lineItemLabel: string;
}

export interface ProductMetaFieldExtraProduct {
  name: string;
  group: string;
  imageUrl: string;
  description: string;
  composition: string;
  durability: string;
  careInstructions: string;
}

export const enum ProductGroupStepType {
  METAFIELD = "METAFIELD",
  SETTING = "SETTING",
}

export type ProductGroupSelectionFromSteps = {
  sourceType: ProductGroupStepType;
  namespace?: string;
  items?: ProductMetaFieldExtraProduct[];
  stepNumber?: number;
  selectorTemplate?: string;
  title: string;
  lineItemLabel?: string;
  productList?: _Product_liquid[];
  quantityToAdd?: number;
  filteringVariantIds?: any;
  gridColumnsDesktop?: string;
  gridColumnsMobile?: string;
  required?: boolean;
  displayPrices?: boolean;
  renderingStrategy?: "per_product" | "per_variant";
  openInfoInNewTab?: boolean;
};

export type ProductStepsListing = ProductGroupSelectionFromSteps[];

export type ReactProductState = {
  product: _Product & { openCartAfterAdd: boolean; gift_card: boolean };
  selectedVariant: _Variant;
  selectedSellingPlan?: _Variant["selling_plan_allocations"][number] | null;
  setSelectedSellingPlan: (selling_plan: _Variant["selling_plan_allocations"][number]) => void;
  setSelectedConfigurableProducts: (
    stepNumber: number,
    product: _Product_liquid,
    variant: _Variant_liquid,
    quantityToAdd: number,
    lineItemLabel: string
  ) => void;
  removeSelectedConfigurableProduct: (stepNumber: number) => void;
  productGroupsSteps: ProductStepsListing;
  setProductGroupSteps: (productGroups: ProductStepsListing) => void;
  resetSelectedConfigurableProducts: () => void;
  resetSelectedProductExtraOptions: () => void;
  selectedConfigurableProducts: SingleSelectedConfigurableProduct[];
  selectedProductExtraOptions: { [T: string]: ProductMetaFieldExtraProduct };
  setSelectedProductExtraOptions: (namespace: string, value: ProductMetaFieldExtraProduct) => void;
  metaobjects?: any;
  formId: string;
  setSelectedVariant: (variantId: number) => void;
  setOptionsVariant: (optionIndex: number, value: string) => void;
  getOptionsVariant: (optionIndex: number, value: string) => _Variant | null;
  options: string[];
  setQuantity: (quantity: number) => void;
  quantity: number;
  setProduct: (product: _Product, openCartAfterAdd?: boolean) => void;
  setSiblingProduct: (product: _Product) => void;
  productSiblings: ProductSiblingGroup[] | null;
  productSiblingSettings: ProductSiblingsSection["settings"] | null;
  productSection: HTMLElement;
  sideEffect: number;
  triggerSideEffect: () => void;
};

export const initProductStore = (
  productSection: HTMLElement,
  productHandle: string,
  formId: string,
  providedProduct?: _Product
) => {
  const product = providedProduct ?? window.useGlobalProducts.getState().getProduct(productHandle);

  const productSiblings = JSONParse<ProductSiblingGroup[]>(
    document.querySelector("[data-product-siblings]")?.innerHTML
  );

  const productSiblingSettings = JSONParse<ProductSiblingsSection["settings"]>(
    document.querySelector<HTMLScriptElement>("[data-product-siblings]")?.dataset
      ?.productSiblingsSettings
  );

  if (!product) {
    return null;
  }

  window.product = product;

  const selling_plan_id =
    window.location.search
      ?.split(/[?&]/gi)
      ?.filter((param) => param && /^selling_plan=/gi.test(param))
      ?.map((searchParams) => searchParams.split("=")?.at(-1))?.[0] ?? "";
  const selling_plan = product?.selected_or_first_available_variant?.selling_plan_allocations?.find(
    (plan) => plan.selling_plan_id === selling_plan_id
  );

  return (set, get) => {
    return {
      productSection,
      product: { ...product, openCartAfterAdd: true },
      productSiblings,
      productSiblingSettings,
      formId,
      selectedVariant: product?.selected_or_first_available_variant,
      selectedSellingPlan: selling_plan,
      options: [...(product?.selected_or_first_available_variant?.options ?? [])],
      quantity: 1,
      sideEffect: 0,
      selectedProductExtraOptions: {},
      setSelectedProductExtraOptions: (namespace, value) => {
        const prevState = get().selectedProductExtraOptions;

        if (prevState[namespace]) {
          prevState[namespace] = value;
        }

        return set({
          selectedProductExtraOptions: { ...prevState, [namespace]: value },
        });
      },
      productGroupsSteps: [],
      setProductGroupSteps: (groups) => {
        return set({
          productGroupsSteps: [...groups],
        });
      },
      resetSelectedProductExtraOptions: () => {
        return set({
          selectedProductExtraOptions: {},
        });
      },
      selectedConfigurableProducts: [],
      setSelectedConfigurableProducts: (
        stepNumber,
        product,
        variant,
        quantityToAdd,
        lineItemLabel
      ) => {
        const prevState = get().selectedConfigurableProducts;
        const currentStepSelectionIndex = prevState.findIndex(
          (step) => step.stepNumber === stepNumber
        );
        const newSelection: SingleSelectedConfigurableProduct = {
          stepNumber: stepNumber,
          product,
          selectedVariant: variant,
          quantityToAdd,
          lineItemLabel,
        };

        if (currentStepSelectionIndex >= 0) {
          const newState = [...prevState];
          newState.splice(currentStepSelectionIndex, 1);

          return set({
            selectedConfigurableProducts: [...newState, newSelection],
          });
        }

        return set({
          selectedConfigurableProducts: [...prevState, newSelection],
        });
      },
      removeSelectedConfigurableProduct: (stepNumber) => {
        const prevState = get().selectedConfigurableProducts;
        const currentStepSelectionIndex = prevState.findIndex(
          (step) => step.stepNumber === stepNumber
        );
        if (currentStepSelectionIndex >= 0) {
          const newState = [...prevState];
          newState.splice(currentStepSelectionIndex, 1);
          return set({
            selectedConfigurableProducts: [...newState],
          });
        }
      },
      resetSelectedConfigurableProducts: () => {
        return set({
          selectedConfigurableProducts: [],
        });
      },
      setQuantity: (quantity) => set({ quantity }),
      setSelectedSellingPlan: (selling_plan) => {
        if (selling_plan) {
          set({ selectedSellingPlan: selling_plan });
          return;
        }

        if (get().product?.product?.requires_selling_plan) {
          set({
            selectedSellingPlan:
              product?.selected_or_first_available_variant?.selling_plan_allocations[0] ?? null,
          });
          return;
        }
        set({ selectedSellingPlan: null });
      },
      setSelectedVariant: (variantId) => {
        const selectedVariant = get().product.variants.find((variant) => variant.id === variantId);

        set({
          selectedVariant,
          options: [...(selectedVariant?.options ?? [])],
          quantity: 1,
        });
      },
      setOptionsVariant: (optionIndex, value) => {
        let options = get().options;
        let selectedSellingPlan = get().selectedSellingPlan;
        options[optionIndex] = value;
        const variants = get().product.variants;
        const selectedVariant =
          variants.find(
            (v) => v.options.every((option, index) => option === options[index]) && v.available
          ) ??
          variants.find((v) => v.options.every((option, index) => option === options[index])) ??
          variants.find((variant) => variant.options[optionIndex] === value && variant.available) ??
          variants.find((variant) => variant.available) ??
          variants.find((variant) => variant);

        options = [...(selectedVariant?.options ?? [])];

        if (
          selectedSellingPlan &&
          !selectedVariant?.selling_plan_allocations?.some(
            (plan) => plan.id === selectedSellingPlan?.id
          )
        ) {
          selectedSellingPlan = selectedVariant?.selling_plan_allocations?.[0] ?? null;
        }

        set({
          options,
          selectedVariant,
          selectedSellingPlan,
          quantity: 1,
        });
      },
      getOptionsVariant: (optionIndex, value) => {
        const options = structuredClone(get().options);
        options[optionIndex] = value;

        return get().product.variants.find((v) =>
          v.options.every((option, index) => option === options[index])
        );
      },
      setProduct: (product, openCartAfterAdd = true) => {
        const selectedVariant =
          product?.variants?.find((variant) => variant.id === get().selectedVariant?.id) ??
          product?.selected_or_first_available_variant ??
          product?.variants?.[0];
        let selectedSellingPlan = get().selectedSellingPlan;

        if (
          selectedSellingPlan &&
          !(product?.selling_plan_groups ?? [])?.some((group) =>
            group?.selling_plans?.some((plan) => `${plan.id}` === `${selectedSellingPlan?.id}`)
          )
        ) {
          selectedSellingPlan = null;
        }

        if (product?.requires_selling_plan && !selectedSellingPlan) {
          selectedSellingPlan = selectedVariant?.selling_plan_allocations?.[0];
        }

        const selling_plan_id =
          get().subscriptionSettings?.default_subscription === "subscription"
            ? selectedVariant?.selling_plan_allocations?.[0]?.id
            : (window.location.search
                ?.split(/[?&]/gi)
                ?.filter((param) => param && /^selling_plan=/gi.test(param))
                ?.map((searchParams) => searchParams.split("=")?.at(-1))?.[0] ?? "");

        const selling_plan = selectedVariant?.selling_plan_allocations?.find(
          (plan) => plan.id === selling_plan_id
        );

        set({
          options: [...(selectedVariant?.options ?? [])],
          product: { ...product, openCartAfterAdd },
          selectedVariant,
          selectedSellingPlan: selectedSellingPlan || selling_plan,
          quantity: 1,
        });
      },
      setSiblingProduct: async (product) => {
        const selectedVariant =
          product.variants.find((variant) => variant.id === get().selectedVariant?.id) ??
          product.selected_or_first_available_variant ??
          product.variants[0];

        const otherQueryParams = window.location.search
          .split(/[?&]/gi)
          .filter((param) => !/^variant=/gi.test(param));

        window.history.replaceState(
          null,
          product.title,
          `${product.handle}?variant=${selectedVariant?.id}${otherQueryParams
            .map((param) => `&${param}`)
            .join("")}`
        );

        set({
          options: [...(selectedVariant?.options ?? [])],
          product: { ...window.structuredClone(product), openCartAfterAdd: true },
          selectedVariant,
          quantity: 1,
        });

        if (productSiblingSettings?.fullpage_reload) {
          // eslint-disable-next-line no-self-assign
          window.location.href = window.location.href.replace(
            /\/products\/.*$/gi,
            `/products/${product.handle}?variant=${selectedVariant?.id}${otherQueryParams
              .map((param) => `&${param}`)
              .join("")}`
          );
          return;
        }

        if (productSiblingSettings?.dynamic_fullpage_reload) {
          await delay(1);
          const sectionIds = [
            "product-data",
            ...[...document.querySelectorAll(".section-content .shopify-section")]
              .filter((section) => {
                const productSection = section.querySelector<HTMLElement>(
                  ".product > [data-product-handle]"
                );
                if (productSection) {
                  productSection.dataset.productHandle = product.handle;
                  productSection.dataset.productSelectedVariant = selectedVariant?.id;
                }
                if (section.querySelector(".apps")) {
                  if (
                    !section.querySelector("[data-bundle-root]") &&
                    !section.querySelector("[data-video-testimonial-root]")
                  ) {
                    return false;
                  }
                }
                return (
                  !productSection &&
                  !section.querySelector("[data-product-siblings]") &&
                  !section.querySelector(".product-add-to-cart-bar")
                );
              })
              .map((section) => section.id.replace(/^shopify-section-/gi, "")),
          ];

          const newSectionData = await Promise.allSettled(
            [...new Array(Math.ceil(sectionIds.length / 4))].map((_, index) => {
              return fetch(
                `${window.location.pathname}?sections=${sectionIds
                  .slice(4 * index, 4 * index + 4)
                  .join(",")}`
              ).then((res) => res.json());
            })
          ).then((data) =>
            data.reduce((acc, res: { [T: string]: any }) => {
              if (res.status === "fulfilled") {
                for (const key in res.value) {
                  acc[key] = res.value[key];
                }
                return acc;
              }
            }, {})
          );

          let updated = false;
          Object.entries(newSectionData).forEach(([key, val]) => {
            if (get().product.handle !== product.handle) {
              return;
            }
            const element = document.getElementById(`shopify-section-${key}`);
            if (element) {
              updated = true;
              element.outerHTML = val as string;
            }
          });

          if (updated) {
            document.querySelectorAll(".section-content .shopify-section").forEach((section) => {
              const productSection = section.querySelector<HTMLElement>(
                ".product > [data-product-handle]"
              );
              if (productSection) {
                productSection.dataset.productHandle = product.handle;
                productSection.dataset.productSelectedVariant = selectedVariant?.id;
              }
            });

            document.dispatchEvent(new Event("DOMContentLoaded"));
            initTheme();
          }
        }
      },
      triggerSideEffect: () => {
        set({ sideEffect: (get()?.sideEffect ?? 0) + 1 });
      },
    };
  };
};

export const initProducts = async () => {
  document
    .querySelectorAll<HTMLElement>("[data-product-handle]:not([data-initialized])")
    .forEach((productSection) => {
      productSection.dataset.initialized = "";
      initProduct(productSection);
    });
};

export type ProductSiblingGroup = {
  title: string;
  title_type: "list" | "metafield";
  options: string;
  products: { id: number; handle: string; image?: string; option?: string }[];
};

export const initProduct = (productSection: HTMLElement) => {
  const productHandle = productSection.dataset?.productHandle;
  const formId = productSection.dataset.productFormId;
  const localProduct: _Product = JSONParse(
    document.querySelector(`[data-product-json="${productHandle}"]`)?.innerHTML
  );

  const useProduct = createReact(
    initProductStore(productSection, productHandle, formId, window.transformProducts(localProduct))
  );

  const sectionSettings = JSONParse(
    productSection.querySelector(`[data-section-settings]`)?.innerHTML
  ) as ProductSection["settings"];

  productSection.querySelectorAll<HTMLElement>(`[data-product-block]`).forEach((block) => {
    const type = block.dataset.productBlock as ProductBlocks["type"] | "side_effects";
    const settings = block.dataset.blockSettings ? JSONParse(block.dataset.blockSettings) : null;
    const variantUpsell = productSection.querySelector<HTMLElement>(
      '[data-product-block="variant_upsell"]'
    );

    const variantUpsellSettings = variantUpsell
      ? JSONParse<ProductBlocksVariant_upsell["settings"]>(variantUpsell.dataset?.blockSettings)
      : null;

    switch (type) {
      case "gift_card_recipient":
        return;
      case "labels": {
        block.innerHTML = "";
        render(<ProductLabels useProduct={useProduct} />, block);
        return;
      }
      case "subscription_selector":
        render(
          <ProductSubscriptionSelector
            useProduct={useProduct}
            settings={settings as ProductBlocksSubscription_selector["settings"]}
          />,
          block
        );
        return;
      case "advanced_subscription":
        render(
          <ProductAdvancedSubscription
            useProduct={useProduct}
            settings={settings as ProductBlocksAdvanced_subscription["settings"]}
          />,
          block
        );
        return;
      case "payment_terms":
        return;
      case "popup":
        return;
      case "@app":
        return;
      case "text":
        return;
      case "title":
        // block.innerHTML = "";
        render(
          <ProductTitle
            useProduct={useProduct}
            settings={settings as ProductBlocksTitle["settings"]}
          />,
          block
        );
        return;
      case "dynamic_text": {
        const content = block.innerHTML;
        block.innerHTML = "";
        block.classList.remove("opacity-0");
        render(
          <ProductDynamicText
            useProduct={useProduct}
            settings={settings as ProductBlocksDynamic_text["settings"]}
            content={content}
          />,
          block
        );
        return;
      }
      case "vendor":
        // block.innerHTML = "";
        render(<ProductVendor useProduct={useProduct} />, block);
        return;
      case "description":
        // block.innerHTML = "";
        render(
          <ProductDescription
            settings={settings as ProductBlocksDescription["settings"]}
            useProduct={useProduct}
          />,
          block
        );
        return;
      case "share":
        // block.innerHTML = "";
        render(<ProductShare useProduct={useProduct} />, block);
        return;
      case "custom_liquid":
        return;
      case "collapsible_tab":
        block.innerHTML = "";
        render(
          <ProductCollapsibleTab
            useProduct={useProduct}
            settings={settings as ProductBlocksCollapsible_tab["settings"]}
          />,
          block
        );
        return;
      case "rating":
        // block.innerHTML = "";
        render(<ProductRating useProduct={useProduct} />, block);
        return;
      case "complementary":
        // block.innerHTML = "";
        render(
          <ProductComplementaryItems
            settings={settings as ProductBlocksComplementary["settings"]}
            useProduct={useProduct}
          />,
          block
        );
        return;
      case "icon_with_text":
        return;
      case "price": {
        // block.innerHTML = "";
        render(<ProductPrice useProduct={useProduct} />, block);
        return;
      }
      case "variant_selector": {
        // block.innerHTML = "";
        render(
          <ProductVariantSelector
            useProduct={useProduct}
            settings={settings as ProductBlocksVariant_selector["settings"]}
            upsellSettings={variantUpsellSettings}
          />,
          block
        );
        return;
      }
      case "product_group_selector": {
        // block.innerHTML = "";
        render(
          <ProductGroupSelector
            useProduct={useProduct}
            settings={settings as ProductBlocksProduct_group_selector["settings"]}
          />,
          block
        );
        return;
      }
      case "variant_upsell": {
        render(
          <ProductVariantUpsell
            useProduct={useProduct}
            settings={settings as ProductBlocksVariant_upsell["settings"]}
          />,
          block
        );
        return;
      }
      case "product_sibling": {
        // block.innerHTML = "";
        render(
          <ProductSibling
            useProduct={useProduct}
            settings={settings as ProductBlocksProduct_sibling["settings"]}
          />,
          block
        );
        return;
      }
      case "sku": {
        // block.innerHTML = "";
        render(<ProductSku useProduct={useProduct} />, block);
        return;
      }
      case "download_link": {
        // block.innerHTML = "";
        render(
          <ProductLeadTime
            useProduct={useProduct}
            settings={settings as ProductBlocksDownload_link["settings"]}
          />,
          block
        );
        return;
      }
      case "quantity_selector": {
        block.innerHTML = "";
        render(<ProductQuantitySelector useProduct={useProduct} />, block);
        return;
      }
      case "buy_buttons": {
        block.innerHTML = "";
        render(
          <ProductBuyButtons
            useProduct={useProduct}
            settings={settings as ProductBlocksBuy_buttons["settings"]}
          />,
          block
        );
        return;
      }
      case "dynamic_buy_buttons": {
        const eventBlock = block.querySelector("[data-product-block-dynamic-button-events]");

        render(
          <DynamicProductBuyButtons
            container={block}
            useProduct={useProduct}
            settings={settings as ProductBlocksDynamic_buy_buttons["settings"]}
          />,
          eventBlock
        );
        return;
      }
      case "inventory": {
        // block.innerHTML = "";
        render(
          <ProductInventorySlider
            settings={settings as ProductBlocksInventory["settings"]}
            useProduct={useProduct}
          />,
          block
        );
        return;
      }
      case "image": {
        render(
          <ProductImage
            settings={settings as ProductBlocksImage["settings"]}
            useProduct={useProduct}
          />,
          block
        );
        return;
      }
      case "pre_order": {
        block.innerHTML = "";
        render(
          <ProductPreOrder
            settings={settings as ProductBlocksPre_order["settings"]}
            useProduct={useProduct}
          />,
          block
        );
        return;
      }
      case "side_effects": {
        // block.innerHTML = "";
        render(
          <ProductSideEffects
            useProduct={useProduct}
            productSection={productSection}
            hasThemeSubscriptionSelector={
              !!productSection.querySelector<HTMLElement>(
                `[data-product-block="subscription_selector"], [data-product-block="advanced_subscription"]`
              )
            }
          />,
          block
        );
        return;
      }
    }
  });

  productSection
    .querySelectorAll<HTMLElement>(`[data-product-media-gallery]`)
    .forEach((galleryElement) => {
      const type = galleryElement.dataset.productMediaGallery as "horizontal" | "vertical";
      const initialized = !!galleryElement.dataset.productGalleryInitialized;

      if (initialized) {
        return;
      }

      galleryElement.innerHTML = "";
      galleryElement.dataset.productGalleryInitialized = "true";

      switch (type) {
        case "horizontal":
          render(
            <ProductGalleryHorizontal settings={sectionSettings} useProduct={useProduct} />,
            galleryElement
          );
          return;
        case "vertical":
          render(
            <ProductGalleryVertical settings={sectionSettings} useProduct={useProduct} />,
            galleryElement
          );
          return;
      }
    });

  const root = document.querySelector<HTMLElement>("[data-product-add-to-cart-bar]");
  const productAddToCartBarUpsellProduct = root?.dataset?.productAddToCartBarUpsellProduct;
  const settings = JSONParse<ProductAddToCartBarSection["settings"]>(
    root?.dataset?.productAddToCartBarSettings
  );

  if (root) {
    root.innerHTML = "";
    render(
      <ProductAddToCartBar
        useProduct={useProduct}
        settings={settings}
        upsellProductHandle={productAddToCartBarUpsellProduct}
      />,
      root
    );
  }
};
